import React, { useState } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';

import { btnType, fontWeight, Heading, headingLevel, IconButton } from '../../../../../../packages';
import { Drawer, drawerType } from '../../../../../../packages/ui/organisms/drawer';
import { NoteCategoryCard } from '../../../../../../packages/ui/templates/note-category-card';

import { getProfile } from '../../../../../../redux/modules/profile/selectors';
import { getAuth } from '../../../../../../redux/modules/auth/selectors';

import { addMemberNotes, updateMemberNotes } from '../../../../../../services/member/member.service';
import { confirmRecommendationNotes } from '../../../../../../services/report-service/reportService.service';
import { NoteCategoriesMock } from '../../../MemberDetail.mock';
import { CONTACT_NOTES } from '../../../../../../constants/CommonConstants';
import { showSnackbar } from '../../../../../../redux/modules/snackbar';
import AddNoteForm from './AddNoteForm';
import { useStyles } from './AddNoteDrawer.styles';
import { MediaManager } from '../../../../../../services/mediaManager/mediaManager';
import StepProcess from '../../../../../../components/StepProcess';
import { CONTACT_NOTES_FLAGS } from '../../../../../../packages/constants/CommonConstants';
import { NOTES_STATUSES } from '../../../../../admin/notes/NotesList.constants';

const AddNoteDrawer = ({
  isOpen,
  note,
  onClose,
  memberId,
  appointmentId,
  recommendationId,
  fetchNotes,
  fetchNotesRecommendations = () => {},
}) => {
  const classes = useStyles();
  const [selectedNote, setNoteCategoryType] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showStepProcess, setShowStepProcess] = useState(false);
  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);
  const [noteId, setNoteId] = useState(null);
  const [selectedLab, setSelectedLab] = useState(null);
  const { meta } = useSelector(getAuth);
  const { profile } = useSelector(getProfile);
  const dispatch = useDispatch();

  const handleClose = () => {
    if (isReadOnlyMode) {
      return;
    }
    if (!isSaving) {
      if (selectedNote) {
        setNoteCategoryType(null);
        setShowStepProcess(false);
        setIsReadOnlyMode(false);
      } else {
        setShowStepProcess(false);
        setIsReadOnlyMode(false);
        onClose();
      }
    }
  };

  const onSelectNoteCategory = item => () => {
    setNoteCategoryType(item);
  };

  const onBackCategories = () => {
    if (isReadOnlyMode) {
      return;
    }
    if (note) {
      setShowStepProcess(false);
      setIsReadOnlyMode(false);
      onClose();
      return;
    }
    setNoteCategoryType(null);
    setShowStepProcess(false);
    setIsReadOnlyMode(false);
  };

  const getNoteTypeName = () => {
    if (note && recommendationId) {
      const noteCategory = NoteCategoriesMock.find(category => category.flag === note.flag);
      return noteCategory?.title?.toLowerCase() || 'note';
    }
    return selectedNote?.title?.toLowerCase() || 'note';
  };

  const renderHeader = () => {
    return (
      <div className={classes.header}>
        <IconButton
          icon="chevron-left"
          variant={btnType.OUTLINE}
          disabled={isReadOnlyMode}
          onClick={onBackCategories}
          className={classes.backBtn}
        />
        <Heading level={headingLevel.M} className={classes.headerTitle} weight={fontWeight.BOLD}>
          {note && !recommendationId ? 'Update note' : `Add ${getNoteTypeName()}`}
        </Heading>
      </div>
    );
  };

  const onSubmit = async payload => {
    const flag = selectedNote?.flag || note?.flag;
    const body = {
      ...payload,
      flag,
      attachments: payload.attachments ?? [],
      recipients: payload.recipients,
      // description: payload.description,
      name: payload.title,
      notes: payload.description,
      patientId: memberId,
      userId: meta?.userId,
      providerId: meta?.userId,

      // relationship: payload.relationship,
      type: flag,
      createdBy: profile.fullName || meta.nickName,
    };
    setIsSaving(true);
    try {
      if (payload.attachments.length > 0) {
        const attachments = [];
        const allPromises = [];
        const withoutOriginalFileAttachemnt = [];
        const originalFileAttachemnt = [];
        const newUplaodedAttachemnt = [];
        payload.attachments.forEach(({ originFile }, index) => {
          if (originFile) {
            originalFileAttachemnt.push(payload.attachments[index]);
            allPromises.push(
              MediaManager.uploadContactNotesMedia(originFile, `${CONTACT_NOTES}/${memberId}/`, e =>
                console.log(e),
              ),
            );
          }
          if (originFile === undefined) {
            withoutOriginalFileAttachemnt.push(payload?.attachments[index]);
          }
        });
        const allPromisesResponded = await Promise.all(allPromises);
        allPromisesResponded.forEach(item => {
          if (item.success) {
            attachments.push(item.response.location);
          }
        });
        if (attachments.length > 0) {
          originalFileAttachemnt.forEach((attachment, index) => {
            newUplaodedAttachemnt.push({
              ...attachment,
              attachmentUrl: attachments[index],
              attachmentSize: attachment?.attachmentSize?.replace('B', ''),
            });
          });
          body.attachments = newUplaodedAttachemnt.concat(withoutOriginalFileAttachemnt);
        }
      }
      if (note && !recommendationId) {
        try {
          const newPayload = { ...note, ...body };
          const response = await updateMemberNotes(newPayload);
          if (response.status === 200) {
            dispatch(
              showSnackbar({
                snackType: 'success',
                snackMessage: response?.data?.message,
              }),
            );
            setNoteId(note?.notesId);
          }
          fetchNotes();
        } catch (error) {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: error?.data?.errors[0].endUserMessage,
            }),
          );
        }
      } else if (appointmentId && recommendationId) {
        try {
          const confirmPayload = {};

          const addNoteResponse = await addMemberNotes(body);
          if (addNoteResponse.status === 200) {
            const response = await confirmRecommendationNotes(confirmPayload, {
              appointmentId: appointmentId,
              recommendationId: recommendationId,
            });
            setNoteId(addNoteResponse?.data?.contactNoteId);
            if (response.status === 200) {
              dispatch(
                showSnackbar({
                  snackType: 'success',
                  snackMessage: 'Recommendation confirmed and note added successfully.',
                }),
              );
              fetchNotes();
              fetchNotesRecommendations();
            }
          }
        } catch (error) {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: error?.data?.errors?.[0]?.endUserMessage || 'Failed to confirm recommendation.',
            }),
          );
        }
      } else {
        try {
          const response = await addMemberNotes(body);
          if (response.status === 200) {
            dispatch(
              showSnackbar({
                snackType: 'success',
                snackMessage: response?.data?.message,
              }),
            );
            setNoteId(response?.data?.contactNoteId);
          }
          fetchNotes();
        } catch (error) {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: error?.data?.errors[0].endUserMessage,
            }),
          );
        }
      }

      const isLabRequest = (selectedNote?.flag || note?.flag) === CONTACT_NOTES_FLAGS.LAB_REQUEST;
      const isAutomaticallySentToLab =
        payload.status === NOTES_STATUSES.AUTOMATICALLY_SEND_TO_LAB &&
        (payload.lab === 'Labcorp' || payload.lab === 'Quest');

      if (isLabRequest && isAutomaticallySentToLab) {
        setSelectedLab(payload.lab);
        setShowStepProcess(true);
        setIsReadOnlyMode(true);
      } else {
        setNoteCategoryType(null);
        setIsReadOnlyMode(false);
        onClose();
      }
      // eslint-disable-next-line no-empty
    } catch (err1) {
      const message = err1?.data?.errors[0].endUserMessage || 'Something went wrong';
      dispatch(showSnackbar({ snackType: 'error', snackMessage: message }));
    }
    setIsSaving(false);
  };

  return (
    <>
      <Drawer
        title={note ? 'Update note' : 'Add note'}
        open={isOpen}
        onClose={handleClose}
        variant={drawerType.FORM}
        className={clsx(classes.drawer, {
          [classes.bodyWhite]: !!selectedNote || !!note,
          [classes.splitLayout]: showStepProcess,
        })}
        footer={<div />}
        submitBtnTitle={note ? 'Save note' : 'Add note'}
        header={selectedNote || note ? renderHeader() : null}
        disableBackdropClick={isReadOnlyMode}
      >
        <div className={clsx(classes.formSection, { [classes.formSectionSplit]: showStepProcess })}>
          {selectedNote || note ? (
            <AddNoteForm
              onSubmit={onSubmit}
              onCancel={onBackCategories}
              noteCategory={selectedNote}
              note={note}
              isSaving={isSaving}
              isRecommendation={recommendationId}
              isReadOnly={isReadOnlyMode}
              hideSaveButton={showStepProcess}
            />
          ) : (
            <div className={classes.categories}>
              {NoteCategoriesMock.map((item, index) => (
                <NoteCategoryCard key={index} {...item} onClick={onSelectNoteCategory(item)} />
              ))}
            </div>
          )}
        </div>
      </Drawer>

      {isOpen && showStepProcess && (
        <div className={classes.externalStepProcess}>
          <StepProcess
            noteId={noteId}
            setIsReadOnlyMode={setIsReadOnlyMode}
            setShowStepProcess={setShowStepProcess}
            onClose={onClose}
            fetchNotes={fetchNotes}
            fetchNotesRecommendations={fetchNotesRecommendations}
            lab={selectedLab}
          />
        </div>
      )}
    </>
  );
};

export default AddNoteDrawer;
